import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Leaf, ArrowRight, Puzzle, Droplets, FlaskConical } from 'lucide-react';
import { Card } from '../Card';
import { cn } from '../../../utils/cn';
import { useReducedMotion } from '../../../hooks/useReducedMotion';
import { DotRating, getDifficultyColor } from './DotRating';
import {
  translateCategory,
  getDifficultyLabel,
} from '../../../pages/PlantOPedia/utils/plantopedia-utils';
import type { SpeciesCardProps } from './SpeciesCard.types';

export const SpeciesRow: React.FC<SpeciesCardProps> = ({ species, onClick, className }) => {
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/plant-o-pedia/species/${species.id}`);
    }
  };

  const difficulty = species.difficulty_level || (species as any).difficulty || 3;
  const waterNeed =
    species.water_need_level || (species as any).optimal_environment?.water_need_level;
  const nutrientDemand =
    species.nutrient_demand || (species as any).optimal_environment?.nutrient_demand;

  return (
    <motion.div
      initial={reducedMotion ? false : { opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={reducedMotion ? {} : { x: 2 }}
      onClick={handleClick}
      role="link"
      tabIndex={0}
      aria-label={`${species.name} — ${translateCategory(species.plant_category || species.plant_type)}, obtížnost ${getDifficultyLabel(difficulty)}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'cursor-pointer focus-visible:outline-2 focus-visible:outline-green-500 focus-visible:outline-offset-2 rounded-2xl',
        className,
      )}
    >
      <Card
        variant="glass"
        padding="none"
        className="hover:border-green-500/30 transition-colors overflow-hidden group"
      >
        <div className="flex items-center gap-4 px-4 py-3">
          {/* Icon */}
          <div className="flex-shrink-0">
            {species.icon ? (
              <img
                src={species.icon}
                alt={species.name}
                className="w-10 h-10 rounded-lg object-cover shadow-sm border border-white dark:border-zinc-700"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  if (target.nextElementSibling) {
                    (target.nextElementSibling as HTMLElement).style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div
              className={cn(
                'items-center justify-center w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
                species.icon ? 'hidden' : 'flex',
              )}
            >
              <Leaf size={20} />
            </div>
          </div>

          {/* Name + Scientific */}
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate group-hover:text-green-600 transition-colors">
              {species.name}
            </span>
            {species.scientific_name && (
              <span className="text-xs text-gray-500 italic truncate">
                {species.scientific_name}
              </span>
            )}
          </div>

          {/* Category badge */}
          <span className="hidden sm:inline-flex text-[11px] px-2 py-0.5 rounded-full bg-white/80 dark:bg-black/50 backdrop-blur-sm text-gray-600 dark:text-zinc-300 font-medium capitalize border border-white/20 whitespace-nowrap flex-shrink-0">
            {translateCategory(species.plant_category || species.plant_type)}
          </span>

          {/* Stats */}
          <div className="hidden md:flex items-center gap-4 flex-shrink-0">
            {/* Difficulty */}
            <div className="flex items-center gap-1.5" title="Obtížnost">
              <Puzzle
                size={12}
                className={getDifficultyColor(difficulty).replace('bg-', 'text-')}
              />
              <DotRating
                value={difficulty}
                activeColor={getDifficultyColor(difficulty)}
                label="Obtížnost"
              />
            </div>

            {/* Water */}
            {waterNeed && (
              <div className="flex items-center gap-1.5" title="Zálivka">
                <Droplets size={12} className="text-cyan-500" />
                <DotRating value={waterNeed} activeColor="bg-cyan-500" label="Zálivka" />
              </div>
            )}

            {/* Nutrients */}
            {nutrientDemand && (
              <div className="flex items-center gap-1.5" title="Živiny">
                <FlaskConical size={12} className="text-teal-500" />
                <DotRating value={nutrientDemand} activeColor="bg-teal-500" label="Živiny" />
              </div>
            )}
          </div>

          {/* Arrow */}
          <ArrowRight
            size={16}
            className="text-green-600 flex-shrink-0 group-hover:translate-x-1 transition-transform"
          />
        </div>
      </Card>
    </motion.div>
  );
};
